import Image from '@/components/common/SpiritoryImage';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faRightToBracket, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import styles from './authenticating.module.scss';
import Link from 'next/link';
import iconOne from '@/public/images/icons/authenticating/1.svg';
import iconTwo from '@/public/images/icons/authenticating/2.svg';
import iconThree from '@/public/images/icons/authenticating/3.svg';
import iconFour from '@/public/images/icons/authenticating/4.svg';
import authenticatingImg from '@/public/images/icons/authenticating//authenticating.svg';
import translationFallback from '@/utilites/translations/translationFallback';
import { useTranslation } from 'next-i18next';
import SearchInGuides from '@/components/common/searchInGuides';
import authenticatingLogoImage from '@/public/images/authenticating/authenticatinglogo.svg';
import authOneImage from '@/public/images/authenticating/auth1.svg';
import authTwoImage from '@/public/images/authenticating/auth2.svg';
import authThreeImage from '@/public/images/authenticating/auth3.svg';
import authFourImage from '@/public/images/authenticating/auth4.svg';
import authFiveImage from '@/public/images/authenticating/auth5.svg';
import authSixImage from '@/public/images/authenticating/auth6.svg';
import authSevenImage from '@/public/images/authenticating/auth7.svg';
import authEightImage from '@/public/images/authenticating/auth8.svg';
import { useRouter } from 'next/router';
import AuthenticatingStructuredData from '../components/structured_data/authenticating_structured_data';

import Head from 'next/head';
import { AuthenticatingProps } from '@/types/props/pages';
import Accordion from 'react-bootstrap/Accordion';
import getBasicStaticSideProps from '@/lib/staticSideProps/getBasicStaticSideProps';
import useBackendUser from '@/hooks/useBackendUser';
import getCanonicalUrl from '@/utilites/seo/get-canonical-url';
import LanguageAlternates from '@/components/seo/LanguageAlternates';

function Authenticating({ user: userSSR }: AuthenticatingProps) {
  const { data: user } = useBackendUser();
  const [open, setOpen] = useState({
    num1: 'none',
    num2: 'none',
    num3: 'none',
  });

  const { t } = useTranslation('authenticating');
  const { t: tCommon } = useTranslation('common');

  const router = useRouter();
  const canonicalUrl = getCanonicalUrl(router);

  const handleSignin = () => {
    if (user) {
      router.push('/dashboard');
    } else {
      router.push('/auth/sign-in');
    }
  };

  const handleSignup = () => {
    if (user) {
      router.push('/dashboard');
    } else {
      router.push('/auth/sign-up');
    }
  };

  return (
    <>
      <Head>
        <title>
          {translationFallback(
            'authenticating_title',
            tCommon('authenticating_title'),
            'Authentication Guide'
          )}
        </title>
        <meta
          name="description"
          content={translationFallback(
            'authenticating_description',
            tCommon('authenticating_description'),
            'A buyer only has to pay once the bottle has been digitally authenticated. Read more about what it means to buy your next bottle securely on Spiritory'
          )}
        />
        <link rel="canonical" href={canonicalUrl} />
        <LanguageAlternates />
        <AuthenticatingStructuredData />
      </Head>
      <div className={styles.allContainer}>
        <div
          className={`bg-blue-gradient d-flex flex-sm-row flex-column-reverse justify-content-end justify-content-sm-between align-items-center mw-auto mh-auto ${styles.mainSection}`}
          style={{ height: '762px', width: '100vw', maxWidth: '100%' }}
        >
          <div className={`${styles.mainText}`}>
            <div className={`${styles.hideDesktop} d-flex flex-column align-items-center gap-2`}>
              <div
                className={`d-flex align-items-start gap-2 ${styles.tagTextContainer}`}
                style={{ maxWidth: 450 }}
              >
                <div>
                  <Image
                    src="/images/authenticating/tag.png"
                    alt="tag-icon"
                    width="57"
                    height="57"
                    style={{
                      objectFit: 'contain',
                      position: 'relative',
                      bottom: 5,
                    }}
                  />
                </div>
                <div>
                  <h1 className="h2 p-0 m-0">
                    {translationFallback(
                      'authenticationGuideTitle',
                      t('authenticationGuideTitle'),
                      'Authentication guide'
                    )}
                  </h1>
                  <div className={`d-flex align-items-start gap-2 ${styles.hideExtraMobile}`}>
                    <FontAwesomeIcon icon={faCheck} className="mt-2 text-primary" />
                    <p className="font-size-20 lh-23 p-0 m-0">
                      {translationFallback(
                        'authenticateBottleText',
                        t('authenticateBottleText'),
                        'Authenticate your bottle:'
                      )}
                      <br />
                      {translationFallback(
                        'safetyPrincipleText',
                        t('safetyPrincipleText'),
                        'At Spiritory our main principle is providing safety to both buyers and sellers. our team verifies each bottle in less than 48 hours.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <div
                  className={`d-flex align-items-start gap-2 ${styles.hideDesktop} ${styles.hideMobile} ${styles.showExtrMobile}`}
                >
                  <FontAwesomeIcon icon={faCheck} className="mt-2 text-primary" />
                  <p className="font-size-20 lh-23 p-0 m-0">
                    {translationFallback(
                      'authenticateBottleText',
                      t('authenticateBottleText'),
                      'Authenticate your bottle:'
                    )}
                    <br />
                    {translationFallback(
                      'safetyPrincipleText',
                      t('safetyPrincipleText'),
                      'At Spiritory our main principle is providing safety to both buyers and sellers. our team verifies each bottle in less than 48 hours.'
                    )}
                  </p>
                </div>
              </div>
              <div
                className={`d-flex align-items-center justify-content-between flex-wrap mt-3 ${styles.buttonContainer}`}
                style={{ width: '100%' }}
              >
                <div
                  style={{ width: '48%' }}
                  onClick={() => {
                    if (user) {
                      router.push('/dashboard');
                    } else {
                      router.push('/auth/sign-in');
                    }
                  }}
                >
                  <button
                    className={`d-flex align-items-center gap-2 btn btn-s-outline-primary px-7`}
                    style={{ padding: '0.7rem 0' }}
                    type="button"
                  >
                    <span>
                      <FontAwesomeIcon icon={faRightToBracket} />
                    </span>
                    {translationFallback('logInButtonText', t('logInButtonText'), 'Log In')}
                  </button>
                </div>
                <div
                  style={{ width: '48%' }}
                  onClick={() => {
                    if (user) {
                      router.push('/dashboard');
                    } else {
                      router.push('/auth/sign-up');
                    }
                  }}
                >
                  <button
                    className={`d-flex align-items-center gap-2 btn btn-s-primary px-7`}
                    style={{ padding: '0.7rem 0' }}
                    type="button"
                  >
                    <span>
                      <FontAwesomeIcon icon={faUserPlus} />
                    </span>
                    {translationFallback('signUpButtonText', t('signUpButtonText'), 'Sign Up')}
                  </button>
                </div>
              </div>
            </div>
            <div className={`container d-flex align-items-end ${styles.hideMobile}`}>
              <Image
                src="/images/authenticating/tag.png"
                alt="tag-icon"
                width="57"
                height="57"
                style={{ objectFit: 'contain' }}
              />
              <h1 className="h2 px-3 font-size-48">
                {translationFallback(
                  'authenticationGuideTitle',
                  t('authenticationGuideTitle'),
                  'Authentication guide'
                )}
              </h1>
            </div>
            <div className={`container px-7 ${styles.hideMobile}`}>
              <div className="d-flex" style={{ maxWidth: 600 }}>
                <FontAwesomeIcon icon={faCheck} className="me-2 text-primary" />
                <p className="font-size-20 font-jakarta font lh-23">
                  {translationFallback(
                    'authenticateBottleText',
                    t('authenticateBottleText'),
                    'Authenticate your bottle:'
                  )}
                  <br />
                  {translationFallback(
                    'safetyPrincipleText',
                    t('safetyPrincipleText'),
                    'At Spiritory our main principle is providing safety to both buyers and sellers. our team verifies each bottle in less than 48 hours.'
                  )}
                </p>
              </div>

              <div className={`block d-flex ${styles.buttonContainer}`}>
                <div
                  style={{
                    background: 'transparent',
                    border: 'none',
                  }}
                  onClick={handleSignin}
                >
                  <button
                    className={`btn btn-s-outline-primary my-2 ${styles.btnSize}`}
                    type="button"
                  >
                    <span>
                      <FontAwesomeIcon icon={faRightToBracket} />
                    </span>
                    {translationFallback('logInButtonText', t('logInButtonText'), 'Log In')}
                  </button>
                </div>
                <div
                  style={{
                    background: 'transparent',
                    border: 'none',
                  }}
                  onClick={handleSignup}
                >
                  <button
                    className={`btn btn-s-primary my-2 ${styles.btnSize}`}
                    type="button"
                    style={{ marginLeft: '12px' }}
                  >
                    <span>
                      <FontAwesomeIcon icon={faUserPlus} />
                    </span>
                    {translationFallback('signUpButtonText', t('signUpButtonText'), 'Sign Up')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className={` ${styles.witheContainerImg2}`}>
            <Image
              className={styles.hideMobile}
              src="/images/authenticating/authenticating2.webp"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
            <Image
              src={'/images/authenticating/authenticatingMobile.webp'}
              className={styles.hideDesktop}
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
          </div>
        </div>
        <div className="d-flex align-items-center text-center">
          <h2
            style={{
              fontFamily: 'Prata',
              fontSize: '36px',
              padding: '1rem 20px',
              height: 'fit-content',
            }}
            className={styles.blueContainerImg2}
          >
            {translationFallback(
              'soldBottleTitle',
              t('soldBottleTitle'),
              "You've sold your bottle? Great! Here's what we do:"
            )}
          </h2>
        </div>

        <div className={styles.whatWeDo}>
          <div className="position-relative my-5 d-flex align-items-center justify-content-center">
            <div
              style={{
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div>
                <h3
                  className={`font-size-32 font-jakarta`}
                  style={{ fontWeight: 'bold', marginBottom: '10px' }}
                >
                  {translationFallback(
                    'qualityStandardsTitle',
                    t('qualityStandardsTitle'),
                    'Our Quality Standards'
                  )}
                </h3>
                <div className={`${styles.hrGold2}`}></div>
                <p className="font-size-20">
                  {translationFallback(
                    'fairnessAndTrustText',
                    t('fairnessAndTrustText'),
                    'At Spiritory, we strive for fairness and trust. We want each deal on our platform, to be like a deal at your local spirits store.'
                  )}
                </p>
              </div>
            </div>
            <div
              className={`font-work-sans ${styles.number1} ${styles.containerTextNumAuth} ${styles.twoNumber}`}
              style={{ marginLeft: '10px' }}
            >
              <p className={styles.twoNumber}>
                {translationFallback('number1Text', t('number1Text'), '1')}
              </p>
            </div>
          </div>
          <div
            className={`d-flex align-items-center justify-content-center ${styles.whatWeDoContainer}`}
          >
            <div>
              <Image
                src={'/images/authenticating/oneImage.webp'}
                width={0}
                height={0}
                sizes="100vw"
                alt=""
              />
            </div>
            <div>
              <div>
                <div>
                  <Image
                    src="/images/authenticating/1-1.png"
                    alt="example"
                    width={33}
                    height={32}
                    sizes="100vw"
                  />
                  <p
                    className={`font-size-24 text-white p-0 m-0`}
                    style={{ marginLeft: '10px', fontWeight: '500' }}
                  >
                    <b>
                      {' '}
                      {translationFallback(
                        'authenticationTitle',
                        t('authenticationTitle'),
                        'Authentication:'
                      )}
                    </b>
                  </p>
                </div>
                <div style={{ paddingTop: '0.5rem' }}>
                  {translationFallback(
                    'authenticationText',
                    t('authenticationText'),
                    'Our team verifies and legit-checks every bottle to fight counterfeits and to give each buyer a full experience of Spiritory standards.'
                  )}
                </div>
              </div>
              <div>
                <div>
                  <Image
                    src="/images/authenticating/1-2.png"
                    alt="example"
                    width={33}
                    height={32}
                    sizes="100vw"
                  />
                  <p
                    className={`font-size-24 text-white p-0 m-0`}
                    style={{ marginLeft: '10px', fontWeight: '500' }}
                  >
                    {translationFallback('conditionTitle', t('conditionTitle'), 'Condition:')}
                  </p>
                </div>
                <div style={{ paddingTop: '0.5rem' }}>
                  {translationFallback(
                    'conditionText',
                    t('conditionText'),
                    "The condition of the box is as important as the condition of the bottle. That's why we make sure both are in MINT condition."
                  )}
                </div>
                <div>
                  <Link href="/condition-guide">
                    <button
                      className={`btn btn-s-outline-primary my-2 font-size-20 ${styles.customButton2}`}
                      type="button"
                      style={{ fontWeight: '600' }}
                    >
                      {translationFallback(
                        'conditionGuidelineButton',
                        t('conditionGuidelineButton'),
                        'Condition Guideline'
                      )}
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`${styles.section1}`}
          style={{ backgroundColor: '#EDEDEE', marginTop: '5rem' }}
        >
          <div>
            <div
              className={`font-work-sans ${styles.number2} ${styles.containerTextNumAuth} ${styles.twoNumber}`}
            >
              2
            </div>
            <div className={styles.authenticateText}>
              <Image src={authenticatingLogoImage} alt="authenticating" width={80} height={80} />
              <span className="p-1">
                {translationFallback(
                  'submitting_your_images',
                  t('submitting_your_images'),
                  'Submitting your images'
                )}
              </span>
              <span className="p-1">
                {translationFallback(
                  'submitting_your_images_text',
                  t('submitting_your_images_text'),
                  'To authenticate the sale, we need following pictures of your bottle.'
                )}
              </span>
              <div className={styles.authenticatingImages}>
                <div>
                  <Image src={authOneImage} alt="authenticating" width={80} height={80} />
                </div>
                <div>
                  <Image src={authTwoImage} alt="authenticating" width={80} height={80} />
                </div>
                <div>
                  <Image src={authThreeImage} alt="authenticating" width={80} height={80} />
                </div>
                <div>
                  <Image src={authFourImage} alt="authenticating" width={80} height={80} />
                </div>
                <div>
                  <Image src={authFiveImage} alt="authenticating" width={80} height={80} />
                </div>
                <div>
                  <Image src={authSixImage} alt="authenticating" width={80} height={80} />
                </div>
                <div>
                  <Image src={authSevenImage} alt="authenticating" width={80} height={80} />
                </div>
                <div>
                  <Image src={authEightImage} alt="authenticating" width={80} height={80} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`${styles.blue} d-flex align-items-stretch justify-content-center`}>
          <div className={`${styles.afterSubmission} `}>
            <div>
              <Image src={authenticatingImg} alt="example" width={0} height={0} sizes="100vw" />
            </div>

            <div className={`${styles.section2}`} style={{ maxWidth: 600 }}>
              <div
                className={`font-work-sans ${styles.number3} ${styles.containerTextNumAuth3} ${styles.twoNumber}`}
                style={{ color: '#FFFFFF33' }}
              >
                3
              </div>
              <div className="d-flex flex-column gap-4">
                <div className={`${styles.containerText1}`}>
                  <h3
                    className={`font-size-36 font-jakarta lh-40 ${styles.bold} `}
                    style={{ marginBottom: '20px' }}
                  >
                    {translationFallback(
                      'afterSubmissionTitle',
                      t('afterSubmissionTitle'),
                      'What happens after submitting my Images?'
                    )}
                  </h3>
                  <div className={styles.borderBottomGold}></div>
                </div>

                <div className="d-flex flex-column gap-4">
                  <div className="d-flex align-items-center gap-2">
                    <div className={`d-flex flex-column align-items-center`}>
                      <Image src={iconOne} alt="example" width={62} height={62} />
                    </div>
                    <p className={'p-0 m-0 font-size-20'}>
                      {translationFallback(
                        'reviewText',
                        t('reviewText'),
                        'Once submitted, our team of experts will review the images.'
                      )}
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className={`d-flex flex-column align-items-center`}>
                      <Image src={iconTwo} alt="example" width={62} height={62} sizes="100vw" />
                    </div>
                    <p className={'p-0 m-0 font-size-20'}>
                      {translationFallback(
                        'responseText',
                        t('responseText'),
                        'We will get back to you within 48 hours via E-Mail or the order page.'
                      )}
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className={`d-flex flex-column align-items-center`}>
                      <Image src={iconThree} alt="example" width={62} height={62} sizes="100vw" />
                    </div>
                    <p className={'p-0 m-0 font-size-20'}>
                      {translationFallback(
                        'authenticationText2',
                        t('authenticationText2'),
                        'Once the bottle is authenticated, the buyer gets charged.'
                      )}
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className={`d-flex flex-column align-items-center`}>
                      <Image src={iconFour} alt="example" width={62} height={62} sizes="100vw" />
                    </div>
                    <p className={'p-0 m-0 font-size-20'}>
                      {translationFallback(
                        'shippingLabelText',
                        t('shippingLabelText'),
                        'We provide the shipping label for the seller.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <SearchInGuides
          becomeTitle={translationFallback(
            'becomeBuyerTitle',
            t('becomeBuyerTitle'),
            'Become a Buyer right now'
          )}
          becomeCallToAct={translationFallback(
            'findDreamBottleText',
            t('findDreamBottleText'),
            'Find your dream bottle today'
          )}
          userId={user?.id ?? null}
        />

        <div className="position-relative my-8 container">
          <div className={`h3 ${styles.bold}`}>
            {translationFallback('faqHeader', t('faqHeader'), 'Frequently asked questions')}
          </div>

          <Accordion defaultActiveKey="0" className={'my-4'}>
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq1_q',
                    t('faqs.faq1_q'),
                    'How does the product authentication works? '
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq1_a',
                  t('faqs.faq1_a'),
                  'Seller has 48 hours to provide a photo.'
                )}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqQuestion1',
                    t('faqQuestion1'),
                    'What happens, if the authentication fails?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqAnswer1',
                  t('faqAnswer1'),
                  "In that case, you'll get a rejection notice for the transaction. If the authentication fails, the bottle does not meet all the conditions of the bottle state, which you can learn more about here."
                )}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="2">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqQuestion2',
                    t('faqQuestion2'),
                    'How long does the authentication take?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqAnswer2',
                  t('faqAnswer2'),
                  'Authentication usually takes from 1 hour to 48 hours.'
                )}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="3">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqQuestion3',
                    t('faqQuestion3'),
                    'How much time does the seller have to provide the photos?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqAnswer3',
                  t('faqAnswer3'),
                  'Seller has 48 hours to provide a photo.'
                )}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq2_q',
                    t('faqs.faq2_q'),
                    'Can I verify a product’s authenticity before purchasing it?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq2_a',
                  t('faqs.faq2_a'),
                  'Once you place your order, the product authentication process begins and can take from 1 to 48 hours. Once the authentication is approved by our team, you can make the payment. This means that you will not have to pay for the product before verifying it with our team of experts. '
                )}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="5">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq3_q',
                    t('faqs.faq3_q'),
                    'Does Spiritory perform quality checks on the products?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq3_a',
                  t('faqs.faq3_a'),
                  'Yes, all products undergo rigorous checks to ensure they meet Spiritory’s quality standards, including unopened seals, intact labels, and original packaging.'
                )}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="6">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq4_q',
                    t('faqs.faq4_q'),
                    'Can I request additional information about a specific product’s authenticity?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq4_a',
                  t('faqs.faq4_a'),
                  'If you need information regarding our quality policies and authentication process you can find our authentication guide at the top of the page under About > Authentication. '
                )}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="7">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq5_q',
                    t('faqs.faq5_q'),
                    'Can I see a picture of the bottle before purchase it?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq5_a',
                  t('faqs.faq5_a'),
                  'You cannot see a picture of the product before purchasing. Our team will ensure the quality and condition of the product through our authentication test.'
                )}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="8">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq6_q',
                    t('faqs.faq6_q'),
                    'What happens if the bottle fails authentication?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq6_a',
                  t('faqs.faq6_a'),
                  'If a bottle fails authentication, the transaction is canceled, and the bottle is not released for sale. The seller may re-submit the product for review if verification materials were insufficient.'
                )}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="9">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq7_q',
                    t('faqs.faq7_q'),
                    'How do I know that my bottle has been authenticated? '
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq7_a',
                  t('faqs.faq7_a'),
                  'You will receive a confirmation email once the bottle passes authentication, ensuring it meets Spiritory’s standards.'
                )}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="10">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq8_q',
                    t('faqs.faq8_q'),
                    'Can I reject the authentcation of my products? '
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq8_a',
                  t('faqs.faq8_a'),
                  'Only approved commercial sellers with an established reputation may opt out of authentication. For private sellers, authentication is mandatory to ensure platform security and buyer trust.'
                )}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>

        <div
          className={`position-relative my-8 ${styles.lastSection}`}
          style={{
            backgroundColor: '#1C5CEB',
            color: '#FFFFFF',
            width: '100vw',
            maxWidth: '100%',
            height: '278px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            className="position-absolute top-0 start-0 text-center"
            style={{ height: '278px', width: '100%' }}
          >
            <div
              className={`d-flex justify-content-between align-items-center ${styles.mainTextLastSection}`}
              style={{ height: '100%', overflow: 'hidden', width: '100%' }}
            >
              <div
                className="d-flex flex-column gap-2"
                style={{ marginLeft: '80px', marginTop: '0px' }}
              >
                <div>
                  <h2 className="h2 text-start">
                    {translationFallback('signUpToday', t('signUpToday'), 'Sign up Today')}
                  </h2>
                  <h5 className="text-start" style={{ marginBottom: '2rem' }}>
                    {translationFallback(
                      'qualityProducts',
                      t('qualityProducts'),
                      'With us you can be sure of the quality of the products you buy'
                    )}
                  </h5>
                </div>
                <div
                  onClick={() => {
                    if (user) {
                      return router.push('/dashboard');
                    }
                    router.push('/auth/sign-up');
                  }}
                  className={`d-flex flex-row ${styles.buttonSection}`}
                >
                  <button
                    className={`btn btn-light font-size-20 lh-25 ${styles.whiteBtnC} ${styles.customButton}`}
                  >
                    <Link
                      href="#"
                      style={{
                        textDecoration: 'none',
                        color: '#FFFFFF',
                        fontWeight: '700',
                      }}
                    >
                      {translationFallback('signUpNow', t('signUpNow'), 'Sign up Now')}
                    </Link>
                  </button>
                  <button
                    onClick={() => {
                      if (user) {
                        return router.push('/dashboard');
                      }
                      router.push('/auth/sign-in');
                    }}
                    className={`btn btn-light font-size-20 lh-25 ${styles.whiteBtn} ${styles.customButton2}`}
                  >
                    <Link
                      href="#"
                      style={{
                        textDecoration: 'none',
                        color: '#1C5CEB',
                        fontWeight: '700',
                      }}
                    >
                      {translationFallback('logInNow', t('logInNow'), 'Log in Now')}
                    </Link>
                  </button>
                </div>
              </div>
              <div
                className={`d-flex align-items-center justify-content-end ${styles.signUpImageContainer}`}
              >
                <Image
                  src={'/images/authenticating/last.svg'}
                  alt="authenticating"
                  width={500}
                  height={500}
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/*

 */}
      </div>
    </>
  );
}

export const getStaticProps = getBasicStaticSideProps(['common', 'authenticating', 'index']);

export default Authenticating;
